import type { SequenceStep } from '@/constants/crm'

import { Trash2 } from 'lucide-react'
import Button from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { OptionSelect } from '@/components/generals/form-selects'
import { SEQUENCE_CHANNELS } from '@/constants/crm'
import PlusIcon from '@/assets/images/_common/plus.svg'

export const MAX_STEPS = 12

interface StepsEditorProps {
    idPrefix: string
    steps: SequenceStep[]
    onChange: (steps: SequenceStep[]) => void
    disabled?: boolean
}

export default function StepsEditor({ idPrefix, steps, onChange, disabled }: StepsEditorProps) {
    function updateStep(index: number, patch: Partial<SequenceStep>) {
        onChange(steps.map((step, position) => position === index ? { ...step, ...patch } : step))
    }

    function addStep() {
        const lastDay = steps[steps.length - 1]?.day ?? 0

        onChange([...steps, { day: steps.length === 0 ? 0 : lastDay + 3, channel: 'Email', subject: '' }])
    }

    return (
        <div className='flex flex-col gap-2'>
            <div aria-hidden className='caption-style text-subtle hidden grid-cols-[72px_120px_minmax(0,1fr)_24px] gap-2 px-0.5 sm:grid'>
                <span>Day</span>
                <span>Channel</span>
                <span>Subject or task</span>
            </div>
            <ol className='flex flex-col gap-2'>
                {steps.map((step, index) => (
                    <li key={index} className='grid grid-cols-[72px_minmax(0,1fr)_24px] items-center gap-2 sm:grid-cols-[72px_120px_minmax(0,1fr)_24px]'>
                        <Input
                            id={`${idPrefix}-day-${index}`}
                            type='number'
                            min={0}
                            max={365}
                            inputMode='numeric'
                            value={String(step.day)}
                            onChange={event => updateStep(index, { day: Math.max(0, Math.min(365, Math.round(Number(event.target.value) || 0))) })}
                            aria-label={`Step ${index + 1} day`}
                            className='tabular-nums'
                            disabled={disabled}
                        />
                        <div className='hidden sm:block'>
                            <OptionSelect
                                id={`${idPrefix}-channel-${index}`}
                                value={step.channel}
                                onChange={value => updateStep(index, { channel: value })}
                                options={SEQUENCE_CHANNELS}
                                disabled={disabled}
                            />
                        </div>
                        <Input
                            id={`${idPrefix}-subject-${index}`}
                            value={step.subject}
                            onChange={event => updateStep(index, { subject: event.target.value })}
                            placeholder={step.channel === 'Call' ? "Call topic" : step.channel === 'Task' ? "Task to do" : "Email subject"}
                            maxLength={160}
                            aria-label={`Step ${index + 1} subject`}
                            disabled={disabled}
                        />
                        <Button
                            variant='ghost'
                            size='icon-sm'
                            onClick={() => onChange(steps.filter((_, position) => position !== index))}
                            aria-label={`Remove step ${index + 1}`}
                            disabled={disabled || steps.length === 1}
                        >
                            <Trash2 aria-hidden className='size-3.5' />
                        </Button>
                        <div className='col-span-3 sm:hidden'>
                            <OptionSelect
                                id={`${idPrefix}-channel-mobile-${index}`}
                                value={step.channel}
                                onChange={value => updateStep(index, { channel: value })}
                                options={SEQUENCE_CHANNELS}
                                disabled={disabled}
                            />
                        </div>
                    </li>
                ))}
            </ol>
            <div>
                <Button variant='secondary' size='sm' onClick={addStep} disabled={disabled || steps.length >= MAX_STEPS}>
                    <PlusIcon aria-hidden className='size-3' />
                    Add step
                </Button>
            </div>
        </div>
    )
}

export function cleanSteps(steps: SequenceStep[]): SequenceStep[] {
    return steps
        .map(step => ({ ...step, subject: step.subject.trim() }))
        .filter(step => step.subject.length > 0)
        .sort((a, b) => a.day - b.day)
}
